/**
 * R7.2.2C — search discovery continuation (reuses R7.1 onsite recommendation brain).
 */
import { useEffect, useState } from "react";
import { CrossSellBlock } from "./CrossSellBlock.js";
import type { CrossSellCandidate } from "./cross-sell-rules.js";
import type { CatalogV2Product } from "./catalog-v2-types.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { fetchOnsiteRecommendations, mapOnsiteToCrossSell } from "./onsite-recommendations-v2.js";

export function SearchDiscoveryContinuationV2({
  lang,
  query,
  hasExactResults,
  anchorProductId,
  catalogById,
  onAdd,
  onOpen,
  onRegisterActionable,
  emitEvent,
}: {
  lang: StoreV2Lang;
  query: string;
  hasExactResults: boolean;
  anchorProductId?: string | null;
  catalogById: Map<string, CatalogV2Product>;
  onAdd: (product: CatalogV2Product) => void;
  onOpen: (product: CatalogV2Product) => void;
  onRegisterActionable?: (products: CatalogV2Product[]) => void;
  emitEvent: (name: string, payload: Record<string, unknown>) => void;
}) {
  const uk = lang !== "en";
  const [items, setItems] = useState<CrossSellCandidate[]>([]);
  const [title, setTitle] = useState<string | null>(null);
  const [subtitle, setSubtitle] = useState<string | null>(null);

  useEffect(() => {
    const surface = hasExactResults && anchorProductId ? "PRODUCT_CROSS_SELL" : "HOME_FOR_YOU";
    void fetchOnsiteRecommendations({
      surface,
      anchorProductId: hasExactResults ? anchorProductId ?? null : null,
      limit: hasExactResults ? 6 : 8,
    })
      .then((res) => {
        if (!res.ok || !res.enabled || res.hidden || !res.items?.length) {
          setItems([]);
          return;
        }
        const mapped = mapOnsiteToCrossSell(res.items, catalogById, lang);
        if (mapped.length < 2) {
          setItems([]);
          return;
        }
        setItems(mapped);
        onRegisterActionable?.(mapped.map((m) => m.product));
        setTitle(
          hasExactResults
            ? uk
              ? "Схожі товари"
              : "Similar products"
            : res.title_uk?.trim() || (uk ? "Популярне зараз" : "Popular now"),
        );
        setSubtitle(
          hasExactResults
            ? uk
              ? "Можливо, вам підійде"
              : "You may also like"
            : uk
              ? "Точного товару не знайшли — ось що може зацікавити"
              : "No exact match — you may find these helpful",
        );
        emitEvent("search_alternatives_shown", {
          query,
          alternative_count: mapped.length,
          has_exact_results: hasExactResults,
          recommendation_surface: surface,
          anchor_product_id: anchorProductId ?? null,
        });
      })
      .catch(() => {
        setItems([]);
      });
  }, [query, hasExactResults, anchorProductId, catalogById, lang, uk, emitEvent, onRegisterActionable]);

  if (items.length === 0) return null;

  return (
    <div data-testid="sv2-search-discovery-continuation">
      {subtitle ? <p className="sv2-muted text-sm m-0 mb-2 px-4">{subtitle}</p> : null}
      <CrossSellBlock
        lang={lang}
        items={items}
        onAdd={onAdd}
        onOpen={(p) => {
          emitEvent("search_alternative_click", {
            query,
            product_id: p.id,
            has_exact_results: hasExactResults,
          });
          onOpen(p);
        }}
        {...(onRegisterActionable ? { onRegisterActionable } : {})}
        variant="pdp"
        {...(title ? { title } : {})}
        placement="SEARCH_CONTINUATION"
        onImpression={(ids) => {
          emitEvent("recommendation_impression", {
            placement: "SEARCH_CONTINUATION",
            product_ids: ids,
            channel: "onsite",
            query,
          });
        }}
      />
      {!hasExactResults ? (
        <div style={{ padding: "0 16px 12px" }}>
          <button
            type="button"
            className="sv2-cta sv2-soft"
            onClick={() => {
              emitEvent("search_reformulated", { from_query: query, surface: "no_result_retry" });
              try {
                document.querySelector<HTMLInputElement>('[data-isolated-search="1"] input[type="search"]')?.focus();
              } catch {
                /* ignore */
              }
            }}
          >
            {uk ? "Спробувати інший пошук" : "Try another search"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
